import { useQuery } from '@apollo/client/react';
import { getContactCenterTenants } from '@api/queries/getContactCenterTenants';

export interface UseCommHubTextingGateProps {
  /** Skip the query entirely (e.g. when an outer gate is already off). */
  skip?: boolean;
}

export interface UseCommHubTextingGateResult {
  /**
   * Fail-closed CommHub texting gate (CCPE-1710): true only once the query
   * has resolved without error and at least one accessible tenant's gate
   * came back enabled (`contactCenterTenants.some(t => t.isCommHubTextingEnabled)`).
   * Loading, error, and empty-list responses all resolve to `false` — never
   * fail open.
   */
  isEnabled: boolean;
  loading: boolean;
}

/**
 * Third, independent AND condition (alongside the `featureContactcenterAgentInboxTexting`
 * LaunchDarkly flag and the `permissionSets.general.TEXTING` permission) gating
 * the Texts tab / `/inbox/texts` route. See CCPE-1710.
 *
 * Reuses `getContactCenterTenants` (the same query Settings pages already
 * fetch tenants with) rather than issuing a dedicated query, so a page that
 * mounts both this hook (via MainLayout) and one of those pages shares a
 * single `contactCenterTenants` request/cache entry instead of two.
 */
export const useCommHubTextingGate = ({
  skip = false,
}: UseCommHubTextingGateProps = {}): UseCommHubTextingGateResult => {
  const { data, loading, error } = useQuery(getContactCenterTenants, {
    skip,
  });

  const isEnabled =
    !loading &&
    !error &&
    !!data?.contactCenterTenants?.some(
      (tenant) => tenant.isCommHubTextingEnabled,
    );

  return { isEnabled, loading };
};
